"use client"

import { useMemo, useState } from "react"
import { Package } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Dialog, DialogClose, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog"

type LegalKey = "terms" | "privacy" | "cookies" | "gdpr"

interface SiteFooterProps {
    dict: {
        title: string;
        description: string;
        legal: {
            title: string;
            links: {
                terms: string;
                privacy: string;
                cookies: string;
                gdpr: string;
            }
            content: {
                terms: { title: string; body: string[] }
                privacy: { title: string; body: string[] }
                cookies: { title: string; body: string[] }
                gdpr: { title: string; body: string[] }
            }
            closeLabel: string
        },
        copyright: string;
    }
}

export function SiteFooter({ dict }: SiteFooterProps) {
    const [activeLegalKey, setActiveLegalKey] = useState<LegalKey | null>(null)
    const legalItems: Array<{ key: LegalKey; label: string }> = [
        { key: "terms", label: dict.legal.links.terms },
        { key: "privacy", label: dict.legal.links.privacy },
        { key: "cookies", label: dict.legal.links.cookies },
        { key: "gdpr", label: dict.legal.links.gdpr },
    ]

    const activeLegalContent = useMemo(() => {
        if (!activeLegalKey) return null
        return dict.legal.content[activeLegalKey]
    }, [activeLegalKey, dict.legal.content])

    return (
        <footer className="relative overflow-hidden bg-linear-to-br from-slate-950 via-slate-900 to-slate-900 text-slate-300 py-14">
            <div className="pointer-events-none absolute inset-0">
                <div className="absolute left-1/4 top-0 h-40 w-40 rounded-full bg-primary/20 blur-3xl" />
                <div className="absolute right-1/4 bottom-0 h-40 w-40 rounded-full bg-teal-300/10 blur-3xl" />
            </div>
            <div className="container mx-auto px-4">
                <div className="max-w-3xl">
                    <div>
                        <div className="flex items-center gap-2 mb-4">
                            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary text-primary-foreground">
                                <Package className="h-5 w-5" />
                            </div>
                            <span className="text-xl font-bold text-white">{dict.title}</span>
                        </div>
                        <p className="text-sm text-slate-300/90 leading-relaxed">{dict.description}</p>
                    </div>
                </div>

                <div className="border-t border-slate-700/60 mt-8 pt-6">
                    <ul className="flex flex-wrap items-center justify-center text-sm">
                        {legalItems.map((item, index) => (
                            <li key={item.key} className="py-1">
                                <button
                                    type="button"
                                    onClick={() => setActiveLegalKey(item.key)}
                                    className="text-slate-300/85 transition-colors hover:text-white"
                                >
                                    <span className={index > 0 ? "border-l border-slate-600 pl-4 ml-4" : ""}>{item.label}</span>
                                </button>
                            </li>
                        ))}
                    </ul>
                </div>

                <div className="border-t border-slate-700/60 mt-8 pt-8 text-sm text-center text-slate-400">
                    <p>&copy; {dict.copyright}</p>
                </div>
            </div>

            <Dialog open={Boolean(activeLegalContent)} onOpenChange={(open) => !open && setActiveLegalKey(null)}>
                <DialogContent className="max-h-[80vh] overflow-y-auto border-primary/20 bg-linear-to-b from-white to-teal-50 sm:max-w-2xl">
                    {activeLegalContent && (
                        <>
                            <DialogHeader>
                                <DialogTitle className="text-primary">{activeLegalContent.title}</DialogTitle>
                            </DialogHeader>
                            <div className="space-y-3 text-sm text-slate-700 leading-relaxed">
                                {activeLegalContent.body.map((paragraph) => (
                                    <p key={paragraph}>{paragraph}</p>
                                ))}
                            </div>
                            <div className="pt-2">
                                <DialogClose asChild>
                                    <Button type="button" className="rounded-full px-6">{dict.legal.closeLabel}</Button>
                                </DialogClose>
                            </div>
                        </>
                    )}
                </DialogContent>
            </Dialog>
        </footer>
    )
}